// Sidebar categories (shared/API.md §41.1): the conversation menu's «Mover a…» (the team's
// categories it may go to + «Nueva categoría…»), a category header's menu («Renombrar» / «Eliminar»
// for custom ones, «Subir» / «Bajar», «Nueva categoría…»), and their two dialogs. Every write is the
// worker's local-first edit: the list changes at once; a refusal puts it back and says so here.
import { useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { data } from '../../../bridge/client';
import { categoryDisplayName } from '../../../core/channelListDerivation';
import { CategoryEdits } from '../../../core/categoryEdits';
import type { CategoryMenuInfo, CategoryTarget } from '../../../data/features/nav';
import { Icon, type IconName } from '../../../design/icons';
import { t, tf } from '../../../i18n/i18n';
import { Button, Dialog } from '../../kit/Kit';
import { ShellContext } from '../../shell/ShellContext';
import { openNav } from './navBus';
import './nav.css';

function useFailToast(): () => void {
  const shell = useContext(ShellContext);
  return () => shell?.toast(t('web.categories.failed'), 'error');
}

function targetIcon(type: string): IconName {
  switch (type) {
    case 'favorites':
      return 'heart';
    case 'channels':
      return 'hash';
    case 'direct_messages':
      return 'person';
    default:
      return 'stack';
  }
}

/** «Mover a…» inside the conversation's context menu: a submenu that opens in place. */
export function MoveToMenuItems({ teamId, channelId, onDone }: { teamId: string | null; channelId: string; onDone(): void }) {
  const [targets, setTargets] = useState<CategoryTarget[] | null>(null);
  const fail = useFailToast();
  if (!teamId) return null;
  if (!targets) {
    return (
      <button
        className="menu-item"
        role="menuitem"
        aria-haspopup="menu"
        data-testid="row-move-to"
        onClick={(e) => {
          e.stopPropagation();
          void data.call('categoryTargets', teamId, channelId).then(setTargets, () => setTargets([]));
        }}
      >
        <Icon name="forward" size={16} /> {t('web.categories.moveTo')}
        <Icon name="chevronRight" size={12} style={{ marginLeft: 'auto' }} />
      </button>
    );
  }
  return (
    <div role="group" aria-label={t('web.categories.moveTo')} className="nav-submenu">
      <div className="nav-section-title">{t('web.categories.moveTo')}</div>
      {targets.map((target) => (
        <button
          key={target.id}
          className="menu-item"
          role="menuitem"
          data-category-id={target.id}
          onClick={() => {
            onDone();
            void data.call('categoryMove', teamId, channelId, target.id).then((ok) => ok || fail(), fail);
          }}
        >
          <Icon name={targetIcon(target.type)} size={16} /> {categoryDisplayName(target.type, target.displayName)}
        </button>
      ))}
      <button
        className="menu-item"
        role="menuitem"
        data-testid="row-new-category"
        onClick={() => {
          onDone();
          openNav({ kind: 'categoryNew', teamId, channelId });
        }}
      >
        <Icon name="plus" size={16} /> {t('web.categories.newCategory')}
      </button>
    </div>
  );
}

/** A category header's menu, at the pointer. `info` is read from the store when it opens. */
export function CategoryMenu({ teamId, categoryId, at, onClose }: { teamId: string; categoryId: string; at: { x: number; y: number }; onClose(): void }) {
  const [info, setInfo] = useState<CategoryMenuInfo | null>(null);
  const fail = useFailToast();
  useEffect(() => {
    let live = true;
    void data.call('categoryMenu', teamId, categoryId).then((i) => live && setInfo(i));
    return () => {
      live = false;
    };
  }, [teamId, categoryId]);
  useEffect(() => {
    const close = () => onClose();
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    const later = setTimeout(() => {
      window.addEventListener('click', close);
      window.addEventListener('contextmenu', close);
    });
    window.addEventListener('keydown', esc);
    window.addEventListener('blur', close);
    return () => {
      clearTimeout(later);
      window.removeEventListener('click', close);
      window.removeEventListener('contextmenu', close);
      window.removeEventListener('keydown', esc);
      window.removeEventListener('blur', close);
    };
  }, [onClose]);
  if (!info) return null;
  const name = categoryDisplayName(info.type, info.displayName);
  const reorder = (direction: 'up' | 'down') => {
    onClose();
    void data.call('categoryReorder', teamId, categoryId, direction).then((ok) => ok || fail(), fail);
  };
  return createPortal(
    <div className="popover sync-parity-menu nav-category-menu" style={{ position: 'fixed', left: Math.min(at.x, window.innerWidth - 248), top: at.y }} role="menu" aria-label={name} onClick={(e) => e.stopPropagation()}>
      {info.canRename ? (
        <button className="menu-item" role="menuitem" onClick={() => (onClose(), openNav({ kind: 'categoryRename', teamId, categoryId, name: info.displayName }))}>
          <Icon name="pencilLine" size={16} /> {t('web.categories.rename')}
        </button>
      ) : null}
      {info.canMoveUp ? (
        <button className="menu-item" role="menuitem" onClick={() => reorder('up')}>
          <Icon name="chevronUp" size={16} /> {t('web.categories.moveUp')}
        </button>
      ) : null}
      {info.canMoveDown ? (
        <button className="menu-item" role="menuitem" onClick={() => reorder('down')}>
          <Icon name="chevronDown" size={16} /> {t('web.categories.moveDown')}
        </button>
      ) : null}
      <button className="menu-item" role="menuitem" onClick={() => (onClose(), openNav({ kind: 'categoryNew', teamId, channelId: null }))}>
        <Icon name="plus" size={16} /> {t('web.categories.newCategory')}
      </button>
      {info.canDelete ? (
        <button className="menu-item danger" role="menuitem" onClick={() => (onClose(), openNav({ kind: 'categoryDelete', teamId, categoryId, name: info.displayName }))}>
          <Icon name="trash" size={16} /> {t('web.categories.delete')}
        </button>
      ) : null}
    </div>,
    document.body,
  );
}

/** «Nueva categoría» / «Renombrar categoría»: one name field, capped like the webapp (22). */
export function CategoryNameDialog({ mode, initial, onSubmit, onClose }: { mode: 'new' | 'rename'; initial: string; onSubmit(name: string): Promise<boolean>; onClose(): void }) {
  const [name, setName] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valid = CategoryEdits.validName(name);
  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    // Local-first: the worker has already drawn it when this resolves; close unless refused.
    const ok = await onSubmit(valid).catch(() => false);
    setBusy(false);
    if (ok) onClose();
    else setError(t('web.categories.failed'));
  };
  return (
    <Dialog
      title={mode === 'new' ? t('web.categories.newTitle') : t('web.categories.renameTitle')}
      onClose={onClose}
      width={420}
      footer={
        <>
          {error ? (
            <span className="people-footer-hint error" role="alert">
              {error}
            </span>
          ) : null}
          <Button kind="quiet" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button kind="primary" disabled={!valid || busy || (mode === 'rename' && valid === initial)} onClick={() => void submit()}>
            {mode === 'new' ? t('web.categories.create') : t('web.categories.save')}
          </Button>
        </>
      }
    >
      <form
        className="people-form"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label className="people-field">
          <span className="people-field-label">{t('web.categories.name')}</span>
          <input
            className="people-input"
            value={name}
            maxLength={CategoryEdits.maxNameLength + 8}
            placeholder={t('web.categories.namePlaceholder')}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            data-testid="category-name"
          />
          <span className="people-field-hint">{tf('web.categories.nameHint', CategoryEdits.maxNameLength)}</span>
        </label>
      </form>
    </Dialog>
  );
}

export function CategoryDeleteDialog({ name, onConfirm, onClose }: { name: string; onConfirm(): void; onClose(): void }) {
  return (
    <Dialog
      title={tf('web.categories.deleteTitle', name)}
      onClose={onClose}
      width={420}
      footer={
        <>
          <Button kind="quiet" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            kind="danger"
            onClick={() => {
              onClose();
              onConfirm();
            }}
          >
            {t('web.categories.delete')}
          </Button>
        </>
      }
    >
      <p className="nav-confirm">{t('web.categories.deleteMessage')}</p>
    </Dialog>
  );
}
